import React from 'react';
import { Button } from './ui/button';
import { Phone, Star, Award, MapPin, CheckCircle, Users, Clock, Shield } from 'lucide-react';
import { advisorInfo } from '../data/mock';
import { analytics, googleAnalytics } from '../utils/analytics';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-pink-50 to-purple-50">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        
        {/* Hero Grid - About Me Section */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left - Profile Photo & Stats */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              {/* Main Profile Card */}
              <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 transform hover:scale-[1.02] transition-all duration-500">
                {/* Profile Image */}
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    <div className="w-56 h-56 md:w-64 md:h-64 mx-auto rounded-3xl overflow-hidden border-4 border-pink-200 shadow-xl">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_perodua-advisor/artifacts/tbac0yri_cf5c0618-872b-4506-8a48-262ed828115a.jpeg"
                        alt="Syasyah Aran - Perodua Sales Advisor"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Top Booking Badge */}
                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-bounce">
                      🏆 TOP BOOKING 2024
                    </div>
                    {/* Verified Badge */}
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Verified Advisor
                    </div>
                  </div>
                </div>

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
                    <Users className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-blue-600">500+</div>
                    <div className="text-xs font-medium text-slate-600">Happy Customers</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
                    <Shield className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-green-600">98%</div>
                    <div className="text-xs font-medium text-slate-600">Loan Approval</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl">
                    <Clock className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-purple-600">7 Hari</div>
                    <div className="text-xs font-medium text-slate-600">Proses Pantas</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl">
                    <Star className="w-6 h-6 text-orange-500 mx-auto mb-1 fill-current" />
                    <div className="text-2xl font-bold text-orange-600">5.0</div>
                    <div className="text-xs font-medium text-slate-600">Rating</div>
                  </div>
                </div>

                {/* TikTok Button */}
                <button 
                  onClick={() => window.open(advisorInfo.tiktokLink, '_blank')}
                  className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-600 hover:via-purple-600 hover:to-indigo-600 text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  Follow @syasyaharan di TikTok
                </button>
              </div>
            </div>
          </div>

          {/* Right - About Me Content */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4 fill-current" />
              <span>Perodua Trusted Advisor</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 leading-tight">
              Hai, Saya{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">
                {advisorInfo.name}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-slate-700 mb-4 font-medium">
              {advisorInfo.title}
            </p>

            {/* Tagline */}
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl p-4 mb-6 inline-block">
              <p className="text-lg md:text-xl font-bold text-pink-700 italic">
                "{advisorInfo.tagline}"
              </p>
            </div>

            {/* About Description */}
            <p className="text-base md:text-lg text-slate-600 mb-6 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Saya seorang <strong>Sales Advisor Perodua</strong> yang berpengalaman membantu ramai pelanggan mendapatkan kereta idaman mereka. 
              Dengan <strong>500+ pelanggan gembira</strong> dan <strong>kadar kelulusan loan 98%</strong>, saya komited untuk memberikan servis terbaik kepada anda!
            </p>

            {/* Service Highlights */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-8">
              <div className="flex items-center space-x-2 bg-green-100 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Loan Senang Lulus</span>
              </div>
              <div className="flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Proses Cepat</span>
              </div>
              <div className="flex items-center space-x-2 bg-purple-100 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">24/7 Support</span>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center justify-center lg:justify-start gap-2 text-slate-600 mb-8">
              <MapPin className="w-5 h-5 text-pink-600" />
              <span className="text-sm md:text-base">{advisorInfo.location}</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={() => {
                  analytics.trackWhatsAppClick('hero_main', null);
                  googleAnalytics.trackWhatsAppConversion('hero_main', null);
                  window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] Hi Syasyah! Saya nak tanya pasal kereta Perodua. Boleh bantu?')}`, '_blank');
                }}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold px-8 py-4 rounded-2xl text-base transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                <Phone className="w-5 h-5 mr-2" />
                WhatsApp Saya
              </Button>
              <Button 
                onClick={() => document.getElementById('models').scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="border-2 border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white font-bold px-8 py-4 rounded-2xl text-base transition-all duration-300"
              >
                Lihat Model Kereta
              </Button>
            </div>

            {/* Contact Info */}
            <div className="mt-8 flex items-center justify-center lg:justify-start gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-lg font-bold text-slate-800">{advisorInfo.phone}</span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default HeroSection;
